package com.arenaproplay.admin.data.auth

import com.arenaproplay.admin.core.config.AppConfig
import com.arenaproplay.admin.core.logger.getLogger
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.json.Json

class AuthRepository(private val httpClient: HttpClient) {
    private val logger = getLogger<AuthRepository>()
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    suspend fun login(email: String, password: String): Result<LoginResponse> {
        return try {
            val response = withTimeout(8000) {
                httpClient.post(AppConfig.getApiUrl("/v1/auth/login")) {
                    contentType(ContentType.Application.Json)
                    setBody(LoginRequest(email, password))
                }
            }
            if (!response.status.isSuccess()) {
                val raw = response.bodyAsText()
                logger.warn("Login failed ${response.status}: $raw")
                return Result.failure(AuthError.fromHttpStatus(response.status, raw))
            }
            val text = response.bodyAsText()
            val parsed = runCatching { json.decodeFromString(LoginResponse.serializer(), text) }
                .getOrElse { return Result.failure(AuthError.UnexpectedShape(text)) }
            Result.success(parsed)
        } catch (e: TimeoutCancellationException) {
            logger.warn("Login timed out — backend not reachable")
            Result.failure(AuthError.Network("Backend not reachable (timeout)"))
        } catch (e: Throwable) {
            logger.error("Login network error", e)
            Result.failure(AuthError.Network(e.message ?: "Network error"))
        }
    }


    suspend fun refresh(refreshToken: String): Result<AuthTokens> {
        return try {
            val response = httpClient.post(AppConfig.getApiUrl("/v1/auth/refresh")) {
                contentType(ContentType.Application.Json)
                setBody(RefreshTokenRequest(refreshToken))
            }
            if (!response.status.isSuccess()) {
                return Result.failure(AuthError.fromHttpStatus(response.status, response.bodyAsText()))
            }
            val tokens = response.body<AuthTokens>()
            Result.success(tokens)
        } catch (e: Throwable) {
            Result.failure(AuthError.Network(e.message ?: "Network error"))
        }
    }

    suspend fun logout(accessToken: String): Result<Unit> {
        return try {
            httpClient.post(AppConfig.getApiUrl("/v1/auth/logout")) {
                header("Authorization", "Bearer $accessToken")
            }
            Result.success(Unit)
        } catch (e: Throwable) {
            Result.success(Unit)
        }
    }
}

sealed class AuthError(message: String) : Throwable(message) {
    class InvalidCredentials(msg: String) : AuthError(msg)
    class Network(msg: String) : AuthError(msg)
    class Server(msg: String) : AuthError(msg)
    class UnexpectedShape(msg: String) : AuthError(msg)

    companion object {
        fun fromHttpStatus(status: HttpStatusCode, body: String): AuthError = when (status.value) {
            401, 403 -> InvalidCredentials(body)
            in 500..599 -> Server(body)
            else -> Server("HTTP ${status.value}: $body")
        }
    }
}
